import { useEffect, useRef } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import Styles from './CKEditor.module.scss';

const CustomCKEditor = ({
    value = '',
    onChange,
    onFocus,
    error = false,
    placeholder = 'Enter Product Description'
}) => {
    const editorRef = useRef(null);
    const observerRef = useRef(null);

    const editorConfig = {
        licenseKey: '',
        placeholder,
        toolbar: {
            items: [
                'heading',
                '|',
                'bold',
                'italic',
                'link',
                'bulletedList',
                'numberedList',
                '|',
                'outdent',
                'indent',
                '|',
                'blockQuote',
                'insertTable',
                'undo',
                'redo'
            ]
        },
        language: 'en',
        image: {
            toolbar: [
                'imageTextAlternative',
                'imageStyle:inline',
                'imageStyle:block',
                'imageStyle:side'
            ]
        },
        table: {
            contentToolbar: [
                'tableColumn',
                'tableRow',
                'mergeTableCells'
            ]
        }
    };

    // Function to remove watermark
    const removeWatermark = () => {
        // Method 1: Remove by class name
        const poweredByElements = document.querySelectorAll('.ck-powered-by-balloon, .ck-powered-by, [class*="powered-by"]');
        poweredByElements.forEach(element => {
            element.remove();
        });

        // Method 2: Remove by link text content
        const allLinks = document.querySelectorAll('a');
        allLinks.forEach(link => {
            if (link.textContent.includes('Powered by CKEditor') ||
                link.href.includes('ckeditor.com')) {
                link.remove();
            }
        });

        // Method 3: Remove balloon panels that contain watermark
        const balloonPanels = document.querySelectorAll('.ck-balloon-panel');
        balloonPanels.forEach(panel => {
            if (panel.innerHTML.includes('Powered by') ||
                panel.innerHTML.includes('ckeditor.com')) {
                panel.remove();
            }
        });
    };

    // Function to update placeholder color
    const updatePlaceholderColor = () => {
        const editorElements = document.querySelectorAll('.ck-editor__editable');
        editorElements.forEach(editor => {
            editor.style.setProperty('--ck-color-engine-placeholder-text', '#797775', 'important');
        });

        // Also target specific placeholder classes
        const placeholderElements = document.querySelectorAll('.ck-placeholder');
        placeholderElements.forEach(element => {
            element.style.color = '#797775';
        });
    };

    useEffect(() => {
        // Set up MutationObserver to watch for watermark elements
        observerRef.current = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.addedNodes.length) {
                    removeWatermark();
                    updatePlaceholderColor();
                }
            });
        });

        // Start observing
        if (editorRef.current) {
            observerRef.current.observe(document.body, {
                childList: true,
                subtree: true
            });
        }

        // Initial cleanup
        removeWatermark();
        updatePlaceholderColor();

        // Periodic cleanup (in case watermark appears later)
        const interval = setInterval(() => {
            removeWatermark();
            updatePlaceholderColor();
        }, 1000);

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            clearInterval(interval);
            removeWatermark(); // Final cleanup
        };
    }, []);

    const handleEditorReady = (editor) => {

        // Apply placeholder color
        updatePlaceholderColor();

        // Remove watermark after editor is ready
        setTimeout(() => {
            removeWatermark();
            updatePlaceholderColor();
        }, 100);
        setTimeout(() => {
            removeWatermark();
            updatePlaceholderColor();
        }, 500);
        setTimeout(() => {
            removeWatermark();
            updatePlaceholderColor();
        }, 1000);

        // Also remove on various editor events
        editor.model.document.on('change', () => {
            removeWatermark();
            updatePlaceholderColor();
        });
        editor.ui.focusTracker.on('change:isFocused', () => {
            removeWatermark();
            updatePlaceholderColor();
        });
    };

    const handleEditorChange = (event, editor) => {
        const data = editor.getData();
        onChange(data);
        removeWatermark(); // Remove on change too
        updatePlaceholderColor();
    };

    const handleEditorFocus = (event, editor) => {
        if (onFocus) onFocus();
        setTimeout(() => {
            removeWatermark();
            updatePlaceholderColor();
        }, 100);
    };

    return (
        <div className={`${Styles.editor_container} ${error ? Styles.error : ''}`}>
            <div className={Styles.editor_wrapper}>
                <CKEditor
                    editor={ClassicEditor}
                    data={value}
                    config={editorConfig}
                    onReady={handleEditorReady}
                    onChange={handleEditorChange}
                    onFocus={handleEditorFocus}
                    onBlur={(event, editor) => {
                        console.log('Blur.', editor);
                    }}
                    onError={(error, { willEditorRestart }) => {
                        if (willEditorRestart) {
                            console.log('Editor crashed and will be restarted:', error);
                        }
                    }}
                />
            </div>
            {/* Add CSS for placeholder color */}
            <style>{`
  .ck-editor__editable {
      --ck-color-engine-placeholder-text: #797775 !important;
  }
  .ck.ck-editor__editable.ck-blurred:before {
      color: #797775 !important;
  }
  .ck-placeholder {
      color: #797775 !important;
  }
`}</style>

        </div>
    );
};

export default CustomCKEditor;